import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import gsap from 'gsap';
import kondifyLogo from '../assets/Kondify letra negra sin fondo recortada.png';
import kondifyEcosistema from '../assets/kondify-ecosistema.png';
import kondifyPhoneImg from '../assets/kondify-app-phone.png';
import kondifyPhoneComunidadImg from '../assets/kondify-app-phone-comunidad.png';
import {
    IoShieldCheckmarkOutline,
    IoCardOutline,
    IoSparklesOutline,
    IoCheckmarkCircle,
    IoLogoWhatsapp,
    IoArrowForwardOutline,
    IoBusinessOutline,
    IoCallOutline,
    IoMailOutline,
    IoCalendarOutline,
    IoChatboxEllipsesOutline,
    IoBarChartOutline,
    IoReceiptOutline,
    IoStatsChartOutline,
    IoWarningOutline,
    IoFolderOpenOutline,
    IoMenuOutline,
    IoCloseOutline,
    IoChevronDownOutline,
    IoChevronUpOutline,
    IoConstructOutline,
    IoPeopleOutline,
    IoFlameOutline
} from 'react-icons/io5';
import './LandingScreen.css';

const LandingScreen = () => {
    const navigate = useNavigate();

    // GSAP Refs
    const navRef = useRef(null);
    const heroRef = useRef(null);
    const showcaseRef = useRef(null);
    const orb1Ref = useRef(null);
    const orb2Ref = useRef(null);
    const orb3Ref = useRef(null);
    const orb4Ref = useRef(null);

    // Estado del Menú Hamburguesa Móvil
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // Estado del Modal de Agendamiento Virtual
    const [isVirtualModalOpen, setIsVirtualModalOpen] = useState(false);

    // Estado del Acordeón FAQ
    const [openFaqIndex, setOpenFaqIndex] = useState(null);

    // Animación de entrada GSAP para Hero, Navbar y Stripe Mesh Gradient continuo
    useEffect(() => {
        const ctx = gsap.context(() => {
            // Navbar entrance
            if (navRef.current) {
                gsap.from(navRef.current, {
                    y: -30,
                    opacity: 0,
                    duration: 0.8,
                    ease: 'power3.out'
                });
            }

            // Hero elements stagger entrance
            gsap.from('.hero-animate', {
                y: 35,
                opacity: 0,
                duration: 0.9,
                stagger: 0.12,
                ease: 'power3.out',
                delay: 0.25
            });

            // Showcase card entrance
            if (showcaseRef.current) {
                gsap.from(showcaseRef.current, {
                    y: 50,
                    opacity: 0,
                    scale: 0.96,
                    duration: 1.1,
                    ease: 'power3.out',
                    delay: 0.65
                });
            }

            // --- STRIPE ANIMATED MESH GRADIENT (GSAP 4-Color Wave Loops) ---
            if (orb1Ref.current) {
                gsap.to(orb1Ref.current, {
                    x: 90,
                    y: -60,
                    scale: 1.25,
                    rotation: 22,
                    duration: 11,
                    repeat: -1,
                    yoyo: true,
                    ease: 'sine.inOut'
                });
            }

            if (orb2Ref.current) {
                gsap.to(orb2Ref.current, {
                    x: -110,
                    y: 70,
                    scale: 1.3,
                    rotation: -25,
                    duration: 13,
                    repeat: -1,
                    yoyo: true,
                    ease: 'sine.inOut'
                });
            }

            if (orb3Ref.current) {
                gsap.to(orb3Ref.current, {
                    x: 75,
                    y: 80,
                    scale: 1.15,
                    rotation: 18,
                    duration: 10,
                    repeat: -1,
                    yoyo: true,
                    ease: 'sine.inOut'
                });
            }

            if (orb4Ref.current) {
                gsap.to(orb4Ref.current, {
                    x: -80,
                    y: -45,
                    scale: 1.2,
                    rotation: -15,
                    duration: 12,
                    repeat: -1,
                    yoyo: true,
                    ease: 'sine.inOut'
                });
            }
        });

        return () => ctx.revert();
    }, []);

    // Google Maps API Loader
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY,
    });

    const [mapCenter, setMapCenter] = useState({ lat: 20.6736, lng: -103.3440 }); // Guadalajara default
    const [markerPos, setMarkerPos] = useState({ lat: 20.6736, lng: -103.3440 });
    const [hasLocation, setHasLocation] = useState(false);

    // Estado del Formulario de Leads
    const [leadData, setLeadData] = useState({
        nombre: '',
        telefono: '',
        email: '',
        condominio: '',
        direccion: '',
        lat: null,
        lng: null,
        unidades: '',
        mensaje: '',
        website_url_hp: '' // Campo Honeypot Anti-Bot
    });
    const [formError, setFormError] = useState('');
    const [submittingLead, setSubmittingLead] = useState(false);
    const [leadSuccess, setLeadSuccess] = useState(false);

    // Geocodificar dirección ingresada por el usuario
    const handleGeocodeAddress = () => {
        if (!leadData.direccion.trim()) {
            setFormError("Ingresa una dirección antes de buscar en el mapa.");
            return;
        }
        if (!window.google || !window.google.maps) {
            setFormError("El servicio de mapas de Google no está disponible temporalmente.");
            return;
        }

        const geocoder = new window.google.maps.Geocoder();
        const fullSearchQuery = `${leadData.direccion}, Mexico`;

        geocoder.geocode({ address: fullSearchQuery }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const loc = results[0].geometry.location;
                const newLat = loc.lat();
                const newLng = loc.lng();
                setMapCenter({ lat: newLat, lng: newLng });
                setMarkerPos({ lat: newLat, lng: newLng });
                setLeadData(prev => ({
                    ...prev,
                    lat: newLat,
                    lng: newLng
                }));
                setHasLocation(true);
                setFormError('');
            } else {
                setFormError("No pudimos localizar esa dirección en el mapa. Intenta agregar más detalles (calle, número, colonia) o arrastra el marcador manualmente.");
            }
        });
    };

    const handleMarkerDragEnd = (e) => {
        const newLat = e.latLng.lat();
        const newLng = e.latLng.lng();
        setMarkerPos({ lat: newLat, lng: newLng });
        setLeadData(prev => ({
            ...prev,
            lat: newLat,
            lng: newLng
        }));
    };

    const toggleFaq = (index) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    // Validar y Enviar Formulario de Prospecto / Lead
    const handleLeadSubmit = async (e) => {
        e.preventDefault();
        setFormError('');

        // 1. Honeypot check (Si los bots llenan este campo oculto, simulamos éxito silencioso)
        if (leadData.website_url_hp) {
            console.warn("Honeypot activado. Abortando bot lead.");
            setLeadSuccess(true);
            return;
        }

        // 2. Validación de campos requeridos
        if (!leadData.nombre.trim() || !leadData.telefono.trim() || !leadData.email.trim() || !leadData.condominio.trim() || !leadData.direccion.trim() || !leadData.unidades) {
            setFormError("Por favor completa los campos obligatorios (*): Nombre, Teléfono, Correo, Condominio, Unidades y Dirección.");
            return;
        }

        // 3. Validación de formato de Correo (Regex Simple)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(leadData.email.trim())) {
            setFormError("Ingresa un correo electrónico válido (ejemplo: contacto@tudominio.com).");
            return;
        }

        // 4. Validación de formato de Teléfono (Mínimo 10 dígitos)
        const cleanedPhone = leadData.telefono.replace(/\D/g, '');
        if (cleanedPhone.length < 10) {
            setFormError("El número de teléfono o WhatsApp debe contener al menos 10 dígitos (ej. 33 1234 5678).");
            return;
        }

        setSubmittingLead(true);
        try {
            // Guardar el Lead en Firestore (sin incluir el campo honeypot)
            const { website_url_hp, ...cleanPayload } = leadData;
            const leadPayload = {
                ...cleanPayload,
                origen: 'landing_web',
                estatus: 'nuevo',
                fechaRegistro: serverTimestamp()
            };

            const docRef = await addDoc(collection(db, 'crm_leads'), leadPayload);

            // AUTO-CREAR en la colección 'condominios' para que aparezca en el SaaS CRM Central
            const activationCode = Math.random().toString(36).substring(2, 8).toUpperCase();
            const newCondoPayload = {
                nombre: leadData.condominio,
                direccion: leadData.direccion,
                timezone: 'America/Mexico_City',
                contactoNombre: leadData.nombre,
                contactoEmail: leadData.email,
                contactoTelefono: leadData.telefono,
                crmStatus: 'Prospecto',
                codigoActivacion: activationCode,
                createdAt: serverTimestamp(),
                unidades: [],
                lat: leadData.lat || null,
                lng: leadData.lng || null,
                proyecciones: {
                    unidades: Number(leadData.unidades) || 0,
                    diaGeneracion: 1,
                    diaVencimiento: 10,
                    cuotaEstimada: 0,
                    amenidades: []
                }
            };
            await addDoc(collection(db, 'condominios'), newCondoPayload);

            // Invocar Cloud Function para notificaciones (Push & Email)
            try {
                const functions = getFunctions();
                const sendLeadAlert = httpsCallable(functions, 'notifyNewLead');
                await sendLeadAlert({ leadId: docRef.id, ...leadPayload });
            } catch (fnErr) {
                console.warn("Aviso de función de alerta de leads:", fnErr);
            }

            setLeadSuccess(true);
        } catch (err) {
            console.error("Error registrando prospecto:", err);
            setFormError("Ocurrió un error al enviar tu solicitud. Inténtalo nuevamente o escríbenos directamente por WhatsApp.");
        } finally {
            setSubmittingLead(false);
        }
    };

    const scrollToSection = (id) => {
        setIsMobileMenuOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
    };

    // Preguntas Frecuentes (FAQ)
    const faqList = [
        {
            q: "¿Kondify incluye la instalación de hardware en caseta o puedo contratar solo el software?",
            a: "Ofrecemos ambas modalidades según las necesidades de tu comunidad: 1) Solución Integral (Llave en Mano): Incluimos e instalamos todo el equipamiento de seguridad física (tótem inteligente para caseta, lectoras de TAGs vehiculares y terminales para guardias) sin costo inicial de adquisición; o 2) Solo Licencia (Software SaaS): Si tu fraccionamiento ya cuenta con caseta o lectores, o únicamente busca digitalizar las finanzas, cobranza en línea con tarjeta, facturación CFDI 4.0, control de visitas por QR y reservas de amenidades, puedes contratar exclusivamente la plataforma digital sin forzar hardware."
        },
        {
            q: "¿Kondify emite facturas CFDI 4.0 automáticamente para los residentes?",
            a: "Así es. Kondify cuenta con integración nativa ante el SAT. En cuanto un residente realiza el pago de su cuota con tarjeta bancaria, el sistema timbra, genera y envía la factura CFDI 4.0 de forma automática, ahorrándole horas de trabajo manual al administrador."
        },
        {
            q: "¿Cómo nos ayuda la plataforma a reducir la morosidad de los vecinos?",
            a: "Combatimos la morosidad mediante tres niveles de automatización: 1) Enviamos recordatorios de pago preventivos y amigables vía notificaciones push y correo electrónico antes de la fecha límite; 2) Calculamos y aplicamos recargos o multas de forma exacta al día siguiente del vencimiento; y 3) Si el reglamento de tu condominio lo estipula, el sistema suspende los accesos automatizados del residente moroso hasta que liquide su saldo."
        },
        {
            q: "¿El Comité puede auditar los movimientos financieros?",
            a: "Totalmente. Kondify promueve la transparencia absoluta en la gestión vecinal. El Comité cuenta con un perfil especial de consulta en tiempo real desde el cual pueden auditar ingresos, egresos, saldos en cuentas bancarias vinculadas y reportes financieros en cualquier momento."
        },
        {
            q: "¿Es posible migrar los datos de mi software actual a Kondify?",
            a: "Sí, nuestro proceso es rápido y guiado de principio a fin. Nuestro equipo de soporte se encarga de importar tu padrón de residentes actual, historial de saldos, cuentas contables y amenidades en menos de 24 horas a través de plantillas inteligentes."
        },
        {
            q: "¿Cuánto cuesta Kondify por unidad o casa?",
            a: "Manejamos dos esquemas accesibles y transparentes sin costos ocultos: 1) Solo Licencia (Software SaaS): Desde $25 MXN mensuales por unidad o lote, ideal para condominios que buscan una administración moderna, cobranza automatizada, facturación del SAT y comunicación vecinal; y 2) Solución Integral (Software + Hardware de Caseta): $125 MXN mensuales por unidad o lote, que incluye la plataforma digital completa más el equipamiento físico de caseta y lectoras de TAGs sin desembolso inicial. Ambas opciones incluyen usuarios ilimitados y app móvil para residentes, administradores, comité y guardias."
        }
    ];

    return (
        <div className="landing-container">
            {/* Global Background Aurora Glows */}
            <div className="landing-bg-glows" aria-hidden="true">
                <div className="bg-glow bg-glow-1"></div>
                <div className="bg-glow bg-glow-2"></div>
                <div className="bg-glow bg-glow-3"></div>
                <div className="bg-glow bg-glow-4"></div>
                <div className="bg-glow bg-glow-5"></div>
            </div>

            {/* --- NAVBAR --- */}
            <header ref={navRef} className="landing-header">
                <div className="landing-nav-content">
                    <div className="landing-brand" onClick={() => navigate('/')} tabIndex="0" aria-label="Ir al Inicio de Kondify">
                        <img 
                            src={kondifyLogo} 
                            alt="Kondify" 
                            className="landing-logo-img" 
                        />
                    </div>

                    <nav className="landing-nav-links desktop-only" aria-label="Navegación Principal">
                        <button onClick={() => scrollToSection('beneficios')} aria-label="Ver Sección de Beneficios">Beneficios</button>
                        <button onClick={() => scrollToSection('como-funciona')} aria-label="Ver Sección Cómo Funciona">Cómo Funciona</button>
                        <button onClick={() => scrollToSection('faq')} aria-label="Ver Preguntas Frecuentes">FAQ</button>
                        <button onClick={() => scrollToSection('contacto')} aria-label="Ir al Formulario de Contacto">Contacto</button>
                    </nav>

                    <div className="landing-nav-actions desktop-only">
                        <button 
                            className="btn-nav-login" 
                            onClick={() => navigate('/login')} 
                            aria-label="Ingresar a la plataforma de Kondify"
                        >
                            Ingresar a mi Condominio
                        </button>
                        <button 
                            className="btn-nav-demo" 
                            onClick={() => setIsVirtualModalOpen(true)} 
                            aria-label="Agendar introducción virtual gratuita"
                        >
                            Intro Virtual (Meet)
                        </button>
                    </div>

                    {/* Botón Menú Hamburguesa Móvil */}
                    <button 
                        className="mobile-hamburger-btn mobile-only" 
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label={isMobileMenuOpen ? "Cerrar menú de navegación" : "Abrir menú de navegación"}
                    >
                        {isMobileMenuOpen ? <IoCloseOutline size={28} /> : <IoMenuOutline size={28} />}
                    </button>
                </div>

                {/* Menú Desplegable Móvil */}
                {isMobileMenuOpen && (
                    <div className="landing-mobile-menu mobile-only">
                        <button onClick={() => { setIsMobileMenuOpen(false); scrollToSection('beneficios'); }}>Beneficios</button>
                        <button onClick={() => { setIsMobileMenuOpen(false); scrollToSection('como-funciona'); }}>Cómo Funciona</button>
                        <button onClick={() => { setIsMobileMenuOpen(false); scrollToSection('faq'); }}>Preguntas Frecuentes</button>
                        <button onClick={() => { setIsMobileMenuOpen(false); scrollToSection('contacto'); }}>Contacto</button>
                        <hr className="mobile-menu-divider" />
                        <button className="btn-mobile-login" onClick={() => { setIsMobileMenuOpen(false); navigate('/login'); }}>
                            Ingresar a mi Condominio
                        </button>
                        <button className="btn-mobile-demo" onClick={() => { setIsMobileMenuOpen(false); setIsVirtualModalOpen(true); }}>
                            Agendar Intro Virtual (15 min)
                        </button>
                    </div>
                )}
            </header>

            {/* --- HERO SECTION --- */}
            <section ref={heroRef} className="landing-hero">
                {/* Stripe-style Animated Diagonal Mesh Canvas Background (GSAP Powered) */}
                <div className="stripe-hero-background">
                    <div className="stripe-skew-wrapper">
                        <div ref={orb1Ref} className="stripe-blob stripe-blob-purple"></div>
                        <div ref={orb2Ref} className="stripe-blob stripe-blob-violet"></div>
                        <div ref={orb3Ref} className="stripe-blob stripe-blob-gold"></div>
                        <div ref={orb4Ref} className="stripe-blob stripe-blob-rose"></div>
                    </div>
                </div>

                <div className="hero-content">
                    {/* H1 Title */}
                    <h1 className="hero-title hero-animate">
                        La Plataforma Inteligente para la{' '}
                        <span className="text-gradient">Gestión de Condominios</span>
                    </h1>

                    {/* Subtítulo */}
                    <p className="hero-subtitle hero-animate">
                        Digitaliza la seguridad, automatiza la cobranza con tarjeta, concilia bancos en segundos y simplifica la convivencia de tu fraccionamiento en una sola experiencia de nivel internacional.
                    </p>

                    {/* CTA Group */}
                    <div className="hero-cta-group hero-animate">
                        <button 
                            className="btn-hero-primary" 
                            onClick={() => setIsVirtualModalOpen(true)}
                            aria-label="Agendar introducción virtual de 15 minutos"
                        >
                            <span>Agendar Intro Virtual (15 min)</span>
                            <IoArrowForwardOutline className="cta-icon" />
                        </button>
                        <button 
                            className="btn-hero-secondary" 
                            onClick={() => scrollToSection('beneficios')}
                            aria-label="Conocer beneficios del sistema"
                        >
                            Conocer Beneficios
                        </button>
                    </div>

                    {/* Trust points */}
                    <div className="hero-trust-bar hero-animate">
                        <span><IoCheckmarkCircle className="trust-icon" /> Sin inversión inicial</span>
                        <span className="trust-dot">•</span>
                        <span><IoCheckmarkCircle className="trust-icon" /> Soporte y asesoría constante</span>
                        <span className="trust-dot">•</span>
                        <span><IoCheckmarkCircle className="trust-icon" /> Transparencia y cumplimiento fiscal</span>
                    </div>

                    {/* Showcase Preview / Interactive Dashboard Frame (Vercel Style) */}
                    <div ref={showcaseRef} className="hero-showcase-container">
                        <div className="showcase-frame">
                            <div className="showcase-image-container">
                                <img 
                                    src={kondifyEcosistema} 
                                    alt="Ecosistema Kondify: Seguridad y Accesos, Finanzas y Cobranza, Comunidad y Administración" 
                                    className="showcase-ecosystem-img"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- FASE 2: 9 BENEFICIOS REESTRUCTURADOS EN 3 CATEGORÍAS --- */}
            <section id="beneficios" className="landing-section">
                <div className="section-header">
                    <span className="section-tag">Todo en una sola plataforma</span>
                    <h2>Beneficios Diseñados para Administradores, Residentes y Comité</h2>
                    <p>Una suite completa dividida en módulos clave que resuelven las necesidades operativas de tu fraccionamiento.</p>
                </div>

                {/* SHOWCASE DOBLE: CELULAR APP A LA IZQUIERDA + SEGURIDAD & FINANZAS A LA DERECHA */}
                <div className="benefits-showcase-layout">
                    {/* Columna Izquierda: Teléfono Móvil */}
                    <div className="benefits-phone-col">
                        <div className="benefits-phone-wrapper">
                            <img 
                                src={kondifyPhoneImg} 
                                alt="App Móvil Kondify para Residentes" 
                                className="benefits-phone-img" 
                            />
                        </div>
                    </div>

                    {/* Columna Derecha: Categorías 1 y 2 */}
                    <div className="benefits-categories-col">
                        {/* CATEGORÍA 1: SEGURIDAD Y ACCESOS */}
                <div className="category-group">
                    <div className="category-header">
                        <div className="category-icon purple"><IoShieldCheckmarkOutline /></div>
                        <h3>Seguridad y Accesos</h3>
                    </div>
                    <div className="benefits-grid-3">
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Residentes y Vigilancia</div>
                            <div className="benefit-icon-box purple"><IoShieldCheckmarkOutline /></div>
                            <h3>Accesos, Pases QR & Bitácora</h3>
                            <p>Generación de pases rápidos de acceso con código QR para invitados, registro digital de vigilancia en caseta y control de paquetería.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Administradores y Comité</div>
                            <div className="benefit-icon-box red"><IoWarningOutline /></div>
                            <h3>Apercibimientos & Sanciones</h3>
                            <p>Registro formal de infracciones al reglamento del condominio, avisos de apercibimiento vecinal y seguimiento ordenado de multas.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Residentes y Administración</div>
                            <div className="benefit-icon-box blue"><IoConstructOutline /></div>
                            <h3>Reportes de Mantenimiento & Tickets</h3>
                            <p>Levantamiento de incidencias por parte de vecinos con fotografía, asignación de tareas a mantenimiento e historial de resolución.</p>
                        </div>
                    </div>
                </div>

                {/* CATEGORÍA 2: FINANZAS Y COBRANZA */}
                <div className="category-group" >
                    <div className="category-header">
                        <div className="category-icon gold"><IoCardOutline /></div>
                        <h3>Finanzas y Cobranza</h3>
                    </div>
                    <div className="benefits-grid-3">
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Residentes y Administradores</div>
                            <div className="benefit-icon-box indigo"><IoCardOutline /></div>
                            <h3>Cuotas & Pagos Seguros con Tarjeta</h3>
                            <p>Emisión de cargos y recibos con cobro en línea mediante tarjeta de crédito o débito respaldado por la tecnología de Stripe.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Administradores y Comité</div>
                            <div className="benefit-icon-box violet"><IoBarChartOutline /></div>
                            <h3>Reportes Financieros & Auxiliares</h3>
                            <p>Balanza de comprobación, estado de resultados, libros mayores y auxiliares contables transparentes exportables al instante.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Administradores y Contadores</div>
                            <div className="benefit-icon-box teal"><IoReceiptOutline /></div>
                            <h3>Conciliación & Facturación CFDI 4.0</h3>
                            <p>Conciliación bancaria automática de depósitos y timbrado de cuotas al instante ante el SAT.</p>
                        </div>
                    </div>
                </div>
                    </div>
                </div>

                {/* SHOWCASE DOBLE 2: COMUNIDAD Y ADMINISTRACIÓN A LA IZQUIERDA + CELULAR APP A LA DERECHA */}
                <div className="benefits-showcase-layout-reverse" style={{ marginTop: '48px' }}>
                    {/* Columna Izquierda: Categoría 3 Comunidad y Administración */}
                    <div className="benefits-categories-col">
                        {/* CATEGORÍA 3: COMUNIDAD Y ADMINISTRACIÓN */}
                <div className="category-group" >
                    <div className="category-header">
                        <div className="category-icon emerald"><IoPeopleOutline /></div>
                        <h3>Comunidad y Administración</h3>
                    </div>
                    <div className="benefits-grid-3">
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Residentes</div>
                            <div className="benefit-icon-box blue"><IoCalendarOutline /></div>
                            <h3>Reserva de Amenidades & Áreas Comunes</h3>
                            <p>Apartado dinámico y control de horarios para albercas, terrazas, palapas, gimnasio y salones con cuotas de uso configurables.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Todos los Vecinos</div>
                            <div className="benefit-icon-box emerald"><IoChatboxEllipsesOutline /></div>
                            <h3>Muro Social & Comunicados Oficiales</h3>
                            <p>Canal de comunicación comunitario, avisos oficiales de la administración con acuse de lectura e historial de publicaciones.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="card-user-badge">Para el Comité y Vecinos</div>
                            <div className="benefit-icon-box amber"><IoStatsChartOutline /></div>
                            <h3>Encuestas & Votaciones del Comité</h3>
                            <p>Votaciones digitales transparentes para la toma de decisiones comunitarias, acuerdos de asamblea y consultas a condóminos.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Administradores</div>
                            <div className="benefit-icon-box sky"><IoFolderOpenOutline /></div>
                            <h3>Librería Documental & Digitalización</h3>
                            <p>Resguardo seguro de reglamentos, actas de asamblea, pólizas, contratos y digitalización integral de la operación.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="card-user-badge">Para Residentes y Administración</div>
                            <div className="benefit-icon-box violet"><IoChatboxEllipsesOutline /></div>
                            <h3>Chat Directo con Caseta y Administración</h3>
                            <p>Mensajería segura en tiempo real para comunicarte de forma privada con vigilancia y la administración.</p>
                        </div>
                        <div className="benefit-card">
                            <div className="card-user-badge">Próximamente</div>
                            <div className="benefit-icon-box gold"><IoSparklesOutline /></div>
                            <h3>Más Beneficios muy Pronto</h3>
                            <p>Trabajamos todos los días para ofrecerte la mejor experiencia; próximamente integraremos nuevas funciones para ti.</p>
                        </div>
                    </div>
                </div>
                    </div>

                    {/* Columna Derecha: Celular App (Comunidad y Accesos Directos) */}
                    <div className="benefits-phone-col">
                        <div className="benefits-phone-wrapper">
                            <img 
                                src={kondifyPhoneComunidadImg} 
                                alt="App Móvil Kondify - Funciones de Comunidad y Accesos Directos" 
                                className="benefits-phone-img" 
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* --- FASE 3: NUEVA SECCIÓN CÓMO FUNCIONA (3 PASOS) --- */}
            <section id="como-funciona" className="landing-section dark-alt">
                <div className="section-header">
                    <span className="section-tag">Puesta en marcha rápida</span>
                    <h2>¿Cómo Funciona Kondify?</h2>
                    <p>Cuatro sencillos pasos para transformar la administración de tu condominio sin complicaciones.</p>
                </div>

                <div className="steps-horizontal-grid">
                    <div className="step-card">
                        <div className="step-number">1</div>
                        <h3>Agenda tu Demo</h3>
                        <p>Platica con un especialista y conoce en vivo la plataforma adaptada al tamaño y requerimientos de tu fraccionamiento.</p>
                    </div>
                    <div className="step-card">
                        <div className="step-number">2</div>
                        <h3>Configuramos tu Condo</h3>
                        <p>Parametrizamos cuotas, cuentas bancarias, amenidades e importamos rápidamente tu padrón de condóminos.</p>
                    </div>
                    <div className="step-card">
                        <div className="step-number">3</div>
                        <h3>Instalación de Equipos</h3>
                        <p>Instalamos tótem de caseta, lectoras de tags y terminales inteligentes en tu fraccionamiento sin costo inicial.</p>
                    </div>
                    <div className="step-card">
                        <div className="step-number">4</div>
                        <h3>Puesta en Marcha</h3>
                        <p>Residentes y guardias descargan la app para disfrutar de la cobranza automática y accesos controlados 24/7.</p>
                    </div>
                </div>
            </section>

            {/* --- FASE 3: LANZAMIENTOS Y PROGRAMA DE FUNDADORES --- */}
            <section className="landing-section">
                <div className="section-header">
                    <span className="section-tag">Próximos lanzamientos</span>
                    <h2>Nuestra Ruta de Implementación</h2>
                    <p>Digitalizamos la convivencia y seguridad de los condominios residenciales de forma planificada y transparente.</p>
                </div>

                <div className="pioneer-cards-grid">
                    <div className="pioneer-grid-glow"></div>
                    <div className="pioneer-card primary-border">
                        <span className="pioneer-badge badge-primary">Próximamente 2027</span>
                        <div className="pioneer-date">2027</div>
                        <h3>Somos Residencial</h3>
                        <span className="pioneer-location">Zapopan, Jalisco</span>
                        <p className="pioneer-desc">
                            Nuestro primer fraccionamiento modelo proyectado para iniciar operaciones automatizadas con tótems inteligentes, administración en la nube y control de accesos vehiculares por TAGs.
                        </p>
                        <ul className="pioneer-highlights">
                            <li>✓ Instalación prioritaria de equipamiento en caseta</li>
                            <li>✓ Migración guiada del padrón e historial de residentes</li>
                            <li>✓ Vinculación de cuentas bancarias y facturación del SAT</li>
                        </ul>
                    </div>

                    <div className="pioneer-card primary-border">
                        <span className="pioneer-badge badge-primary">2 Lugares Disponibles</span>
                        <div className="pioneer-date">Programa Piloto</div>
                        <h3>Condominios Fundadores</h3>
                        <span className="pioneer-location">Zona Metropolitana de Guadalajara</span>
                        <p className="pioneer-desc">
                            Buscamos a los siguientes 2 desarrollos o administraciones pioneras que deseen sumarse al programa piloto de este trimestre bajo condiciones especiales de por vida.
                        </p>
                        <ul className="pioneer-highlights">
                            <li>★ 20% de descuento vitalicio en tu suscripción</li>
                            <li>★ Prioridad en fila de instalación física y soporte VIP</li>
                            <li>★ Canal de comunicación directo con los fundadores</li>
                        </ul>
                        <button className="btn-pioneer-cta" onClick={() => scrollToSection('contacto')}>
                            Postular mi Condominio
                        </button>
                    </div>
                </div>
            </section>

            {/* --- FORMULARIO DE DEMO / CONTACTO CON SEGURIDAD FASE 4 --- */}
            <section id="contacto" className="landing-section">
                <div className="contact-wrapper">
                    <div className="contact-info-col">
                        <span className="section-tag">Presentación Formal</span>
                        <h2>Agenda una Demo Presencial en tu Condominio</h2>
                        <p>¿Buscas una presentación en vivo ante el Comité de Administración o la Asamblea? Solicita una visita presencial. Si prefieres resolver dudas rápidas en línea, agenda una <button onClick={(e) => { e.preventDefault(); setIsVirtualModalOpen(true); }} style={{ background: 'none', border: 'none', padding: 0, color: '#7E66FC', textDecoration: 'underline', fontWeight: '600', cursor: 'pointer', font: 'inherit' }}>Intro Virtual</button> con los botones de arriba.</p>

                        <div className="contact-direct-list">
                            <div className="contact-direct-item">
                                <IoCallOutline className="contact-icon" />
                                <div>
                                    <strong>Llámanos o escríbenos:</strong>
                                    <p>+52 33 2729 0654</p>
                                </div>
                            </div>
                            <div className="contact-direct-item">
                                <IoMailOutline className="contact-icon" />
                                <div>
                                    <strong>Correo Electrónico:</strong>
                                    <p>ventas@kondify.mx</p>
                                </div>
                            </div>
                            <div className="contact-direct-item">
                                <IoBusinessOutline className="contact-icon" />
                                <div>
                                    <strong>Cobertura:</strong>
                                    <p>Atención y soporte en todo México</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="contact-form-col">
                        {leadSuccess ? (
                            <div className="lead-success-card">
                                <IoCheckmarkCircle className="success-icon" style={{ fontSize: '3rem', color: '#059669', marginBottom: '8px' }} />
                                <h3 style={{ fontSize: '1.4rem', color: '#0F172A', marginBottom: '8px' }}>¡Datos de Ubicación Registrados!</h3>
                                <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '20px' }}>
                                    Paso 1 completado. Ahora, por favor <strong>selecciona el día y la hora</strong> para nuestra visita presencial a tu condominio en el calendario a continuación:
                                </p>
                                <div style={{ height: '550px', width: '100%', borderRadius: '16px', overflow: 'hidden', border: '1px solid #E2E8F0', background: '#ffffff', marginBottom: '20px' }}>
                                     <iframe
                                         src={`https://cal.com/kondify/demo-presencial-kondify?name=${encodeURIComponent(leadData.nombre)}&email=${encodeURIComponent(leadData.email)}&location=${encodeURIComponent(JSON.stringify({ value: 'attendeeInPerson', optionValue: leadData.direccion }))}&theme=light`}
                                         title="Agendar Demo Presencial"
                                         style={{ width: '100%', height: '100%', border: 'none' }}
                                     ></iframe>
                                </div>
                                <button className="btn-reset-lead" onClick={() => { setLeadSuccess(false); setFormError(''); setLeadData({ nombre: '', telefono: '', email: '', condominio: '', direccion: '', lat: null, lng: null, unidades: '', mensaje: '', website_url_hp: '' }); }}>
                                    Enviar otra solicitud
                                </button>
                            </div>
                        ) : (
                            <form className="landing-contact-form" onSubmit={handleLeadSubmit} noValidate>
                                <h3>Solicitar Demostración Presencial</h3>

                                {/* FASE 4: Banner de Error Inline (reemplazo de alert) */}
                                {formError && (
                                    <div className="form-error-banner" role="alert">
                                        <IoWarningOutline size={20} />
                                        <span>{formError}</span>
                                    </div>
                                )}

                                {/* FASE 4: Campo Honeypot Invisible Anti-Bot */}
                                <input
                                    type="text"
                                    name="website_url_hp"
                                    value={leadData.website_url_hp || ''}
                                    onChange={(e) => setLeadData({ ...leadData, website_url_hp: e.target.value })}
                                    style={{ display: 'none' }}
                                    tabIndex="-1"
                                    autoComplete="off"
                                />

                                <div className="form-group">
                                    <label htmlFor="lead-nombre">Nombre Completo *</label>
                                    <input
                                        id="lead-nombre"
                                        type="text"
                                        placeholder="Ej. Ing. Carlos Mendoza"
                                        value={leadData.nombre}
                                        onChange={(e) => setLeadData({ ...leadData, nombre: e.target.value })}
                                        required
                                        aria-required="true"
                                    />
                                </div>

                                <div className="form-row-2">
                                    <div className="form-group">
                                        <label htmlFor="lead-telefono">Teléfono / WhatsApp *</label>
                                        <input
                                            id="lead-telefono"
                                            type="tel"
                                            placeholder="33 1234 5678"
                                            value={leadData.telefono}
                                            onChange={(e) => setLeadData({ ...leadData, telefono: e.target.value })}
                                            required
                                            aria-required="true"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="lead-email">Correo Electrónico *</label>
                                        <input
                                            id="lead-email"
                                            type="email"
                                            placeholder="carlos@ejemplo.com"
                                            value={leadData.email}
                                            onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                                            required
                                            aria-required="true"
                                        />
                                    </div>
                                </div>

                                <div className="form-row-2">
                                    <div className="form-group">
                                        <label htmlFor="lead-condominio">Nombre del Condominio *</label>
                                        <input
                                            id="lead-condominio"
                                            type="text"
                                            placeholder="Ej. Residencial Las Palmas"
                                            value={leadData.condominio}
                                            onChange={(e) => setLeadData({ ...leadData, condominio: e.target.value })}
                                            required
                                            aria-required="true"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="lead-unidades">Número de Casas / Unidades *</label>
                                        <input
                                            id="lead-unidades"
                                            type="number"
                                            placeholder="Ej. 60"
                                            value={leadData.unidades}
                                            onChange={(e) => setLeadData({ ...leadData, unidades: e.target.value ? Number(e.target.value) : '' })}
                                            required
                                            aria-required="true"
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="lead-direccion">Dirección del Condominio *</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <input
                                            id="lead-direccion"
                                            type="text"
                                            placeholder="Calle, número, colonia, código postal..."
                                            value={leadData.direccion}
                                            onChange={(e) => setLeadData({ ...leadData, direccion: e.target.value })}
                                            required
                                            aria-required="true"
                                            style={{ flex: 1 }}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleGeocodeAddress}
                                            style={{
                                                background: '#7E66FC',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '12px',
                                                padding: '0 16px',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Buscar en Mapa
                                        </button>
                                    </div>
                                </div>

                                {/* Visor de Mapa de Google */}
                                {isLoaded ? (
                                    <div style={{ height: '220px', width: '100%', borderRadius: '16px', overflow: 'hidden', border: '1px solid #E2E8F0', margin: '16px 0' }}>
                                        <GoogleMap
                                            mapContainerStyle={{ height: '100%', width: '100%' }}
                                            center={mapCenter}
                                            zoom={15}
                                            options={{
                                                disableDefaultUI: true,
                                                zoomControl: true
                                            }}
                                        >
                                            <Marker
                                                position={markerPos}
                                                draggable={true}
                                                onDragEnd={handleMarkerDragEnd}
                                            />
                                        </GoogleMap>
                                    </div>
                                ) : (
                                    <div style={{ height: '220px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', color: '#64748B', fontSize: '0.9rem', margin: '16px 0' }}>
                                        Cargando mapa interactivo...
                                    </div>
                                )}

                                <div className="form-group">
                                    <label htmlFor="lead-mensaje">Comentarios adicionales o dudas</label>
                                    <textarea
                                        id="lead-mensaje"
                                        rows="3"
                                        placeholder="Deseamos revisar el cobro automático con tarjeta y la facturación CFDI 4.0..."
                                        value={leadData.mensaje}
                                        onChange={(e) => setLeadData({ ...leadData, mensaje: e.target.value })}
                                    ></textarea>
                                </div>

                                <button 
                                    type="submit" 
                                    className="btn-submit-lead" 
                                    disabled={submittingLead}
                                    aria-label="Enviar formulario de solicitud de demostración presencial"
                                >
                                    {submittingLead ? 'Enviando solicitud...' : 'Enviar Solicitud de Demo Presencial'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* --- FASE 3: SECCIÓN FAQ (ACORDEÓN 2 COLUMNAS) --- */}
            <section id="faq" className="landing-section dark-alt">
                <div className="section-header">
                    <span className="section-tag">Resolvemos tus dudas</span>
                    <h2>Preguntas Frecuentes</h2>
                    <p>Encuentra respuestas rápidas a las consultas más comunes sobre la implementación de Kondify.</p>
                </div>

                <div className="faq-grid-container">
                    <div className="faq-column">
                        {faqList.slice(0, 3).map((item, idx) => {
                            const actualIdx = idx;
                            return (
                                <div key={actualIdx} className={`faq-item ${openFaqIndex === actualIdx ? 'active' : ''}`}>
                                    <button className="faq-question" onClick={() => toggleFaq(actualIdx)} aria-expanded={openFaqIndex === actualIdx}>
                                        <span>{item.q}</span>
                                        {openFaqIndex === actualIdx ? <IoChevronUpOutline size={20} /> : <IoChevronDownOutline size={20} />}
                                    </button>
                                    {openFaqIndex === actualIdx && (
                                        <div className="faq-answer">
                                            <p>{item.a}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className="faq-column">
                        {faqList.slice(3, 6).map((item, idx) => {
                            const actualIdx = idx + 3;
                            return (
                                <div key={actualIdx} className={`faq-item ${openFaqIndex === actualIdx ? 'active' : ''}`}>
                                    <button className="faq-question" onClick={() => toggleFaq(actualIdx)} aria-expanded={openFaqIndex === actualIdx}>
                                        <span>{item.q}</span>
                                        {openFaqIndex === actualIdx ? <IoChevronUpOutline size={20} /> : <IoChevronDownOutline size={20} />}
                                    </button>
                                    {openFaqIndex === actualIdx && (
                                        <div className="faq-answer">
                                            <p>{item.a}</p>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* --- BOTÓN FLOTANTE WHATSAPP --- */}
            <a
                href="https://wa.me/523327290654?text=Hola,%20me%20interesa%20solicitar%20una%20demo%20de%20Kondify%20para%20mi%20condominio"
                target="_blank"
                rel="noopener noreferrer"
                className="floating-whatsapp-btn"
                aria-label="Contactar por WhatsApp a Kondify"
                title="Hablar por WhatsApp"
            >
                <IoLogoWhatsapp />
                <span className="wa-tooltip">¿Dudas? Habla por WhatsApp</span>
            </a>

            {/* --- FOOTER CORPORATIVO (FASE 6 ENLACES LEGALES) --- */}
            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-col-brand">
                        <img 
                            src={kondifyLogo} 
                            alt="Kondify Logo" 
                            className="footer-logo-img" 
                        />
                        <p>Plataforma SaaS integral para la administración inteligente de condominios, cobros integrados y contabilidad automatizada.</p>
                    </div>
                    <div className="footer-col">
                        <h4>Enlaces Rápidos</h4>
                        <button onClick={() => scrollToSection('beneficios')}>Beneficios</button>
                        <button onClick={() => scrollToSection('como-funciona')}>Cómo Funciona</button>
                        <button onClick={() => scrollToSection('faq')}>Preguntas Frecuentes</button>
                        <button onClick={() => navigate('/login')}>Ingresar a App</button>
                    </div>
                    <div className="footer-col">
                        <h4>Contacto & Ventas</h4>
                        <p>📧 ventas@kondify.mx</p>
                        <p>📞 +52 33 2729 0654</p>
                        <p>📍 Jalisco, México</p>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>© {new Date().getFullYear()} Kondify. Todos los derechos reservados.</p>
                    <div className="footer-legal-links">
                        <Link to="/privacidad">Aviso de Privacidad</Link>
                        <span>•</span>
                        <Link to="/terminos">Términos y Condiciones</Link>
                    </div>
                </div>
            </footer>
            {/* --- MODAL PARA AGENDAMIENTO VIRTUAL INTRO --- */}
            {isVirtualModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(15, 23, 42, 0.75)',
                    backdropFilter: 'blur(8px)',
                    zIndex: 2000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px'
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '24px',
                        width: '100%',
                        maxWidth: '850px',
                        height: '85vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        position: 'relative'
                    }}>
                        {/* Header del Modal */}
                        <div style={{
                            padding: '16px 24px',
                            borderBottom: '1px solid #E2E8F0',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            background: '#F8FAFC'
                        }}>
                            <h3 style={{ margin: 0, color: '#0F172A', fontSize: '1.25rem', fontWeight: '700' }}>Agendar Sesión de Introducción Virtual</h3>
                            <button
                                onClick={() => setIsVirtualModalOpen(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#64748B',
                                    cursor: 'pointer',
                                    padding: '4px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <IoCloseOutline size={28} />
                            </button>
                        </div>
                        {/* Iframe de Cal.com */}
                        <div style={{ flex: 1, width: '100%', background: '#ffffff' }}>
                            <iframe
                                src="https://cal.com/kondify/30min?theme=light"
                                title="Agendar Intro Virtual"
                                style={{ width: '100%', height: '100%', border: 'none' }}
                            ></iframe>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LandingScreen;
